import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ChangePasswordDialog from '../common/ChangePasswordDialog';
import EmailTestDialog from '../common/EmailTestDialog';
import BillingReportDialog from '../common/BillingReportDialog';
import SmsTestPanel from '../SmsTestPanel';
import { transferOrganizationData, exportOrganizationLeads, getOrganizationsByUser } from '../../api';
import CreateAgenturDialog from '../common/CreateAgenturDialog';
import { createAgentur, downloadAgenturLeadsByPlzExcel } from '../../api';
import ProjectManagementModal from '../common/ProjectManagementModal';
import ProjectExportModal from '../common/ProjectExportModal';

function SettingsPanel({ 
  isOpen, 
  onClose, 
  isAdmin, 
  isCallcenterLeiter,
  isTeamlead, 
  isOrgLeader, 
  user, 
  onOpenOrganizationManagement, // Wird nicht mehr verwendet
  onOpenMembersManagement, // Wird nicht mehr verwendet
  onViewOrganizationLeads // Wird nicht mehr verwendet
}) {
  const navigate = useNavigate();
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [showEmailTestDialog, setShowEmailTestDialog] = useState(false);
  const [showBillingReportDialog, setShowBillingReportDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [showCreateAgenturDialog, setShowCreateAgenturDialog] = useState(false);
  const [agenturLoading, setAgenturLoading] = useState(false);
  const [showProjectManagementModal, setShowProjectManagementModal] = useState(false);
  const [showProjectExportModal, setShowProjectExportModal] = useState(false);
  const [currentOrganizationId, setCurrentOrganizationId] = useState(null);
  const [plzInput, setPlzInput] = useState('');
  const [plzExporting, setPlzExporting] = useState(false);

  // Aktuelle Organisation des Benutzers ermitteln
  useEffect(() => {
    const determineCurrentOrganization = async () => {
      if (isAdmin) {
        // Admin: Verwende organization_id aus user-Objekt oder null (kann dann im Backend geprüft werden)
        setCurrentOrganizationId(user?.organization_id || null);
      } else if (isOrgLeader && user?.id) {
        // Organisationsleiter: Lade Organisationen und verwende die erste oder die aus user.organization_id
        try {
          const response = await getOrganizationsByUser(user.id);
          if (response.success && response.organizations && response.organizations.length > 0) {
            // Verwende organization_id aus user, falls vorhanden und in der Liste, sonst die erste
            const orgId = user?.organization_id && response.organizations.some(org => org.id === user.organization_id)
              ? user.organization_id
              : response.organizations[0].id;
            setCurrentOrganizationId(orgId);
          } else if (user?.organization_id) {
            setCurrentOrganizationId(user.organization_id);
          }
        } catch (error) {
          console.error('Fehler beim Laden der Organisationen:', error);
          // Fallback: Verwende organization_id aus user-Objekt
          setCurrentOrganizationId(user?.organization_id || null);
        }
      } else {
        // Andere Rollen: Verwende organization_id aus user-Objekt
        setCurrentOrganizationId(user?.organization_id || null);
      }
    };

    determineCurrentOrganization();
  }, [isAdmin, isOrgLeader, user?.id, user?.organization_id]);

  const handleCreateUser = () => {
    // Close panel first to avoid UI glitches
    onClose();
    
    // Add a small delay to ensure panel is closed before navigation
    setTimeout(() => {
      if (isTeamlead && user?.team?.id) {
        navigate(`/create-user?teamId=${user.team.id}`);
      } else {
        navigate('/create-user');
      }
    }, 100);
  };
  
  // Function for navigating to organization leads using route
  const handleOrganizationLeads = () => {
    onClose();
    setTimeout(() => {
      navigate('/organization-leads');
    }, 100);
  };

  // Function for navigating to organization management using route
  const handleOrganizationManagement = () => {
    onClose();
    setTimeout(() => {
      navigate('/organization-management');
    }, 100);
  };

  // Function for navigating to members management using route
  const handleMembersManagement = () => {
    onClose();
    setTimeout(() => {
      navigate('/organization-members');
    }, 100);
  };

  const handlePasswordChangeSuccess = (message) => {
    setSuccessMessage(message);
    setShowChangePasswordDialog(false);
    
    // Erfolgsmeldung nach 3 Sekunden ausblenden
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  const handleChangePassword = () => {
    setShowChangePasswordDialog(true);
    setSuccessMessage(''); // Vorherige Meldungen löschen
  };

  const handleBillingReport = () => {
    setShowBillingReportDialog(true);
  };

  // Handler für Termin-Dateien Download (verwendet automatisch die aktuelle Organisation)
  const handleDownloadTerminFiles = async (targetDate = null) => {
    if (!currentOrganizationId) {
      setError('Keine Organisation gefunden. Bitte wählen Sie eine Organisation aus.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Verwende transferOrganizationData für alle Organisationen (funktioniert für BEN und Energie+)
      const response = await transferOrganizationData(user.id, currentOrganizationId, targetDate);
      
      // JSON-Output in Console ausgeben
      console.log(`=== TERMIN-DATEIEN DOWNLOAD (Organisation ${currentOrganizationId}) ===`);
      console.log(JSON.stringify(response, null, 2));
      console.log('==============================================');
      
      if (response.success) {
        setSuccessMessage(`Termin-Dateien erfolgreich heruntergeladen! ${response.count || 0} Termine gefunden.`);
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError('Fehler beim Download: ' + response.error);
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      setError('Fehler beim Download: ' + error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  // Handler für Organisation Leads Export (für Organisationsleiter)
  const handleExportOrganizationLeads = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await exportOrganizationLeads(user.id);
      
      // JSON-Output in Console ausgeben
      console.log('=== ORGANISATION LEADS EXPORT ===');
      console.log(JSON.stringify(response, null, 2));
      console.log('==============================================');
      
      if (response.success) {
        setSuccessMessage(`Organisation Leads erfolgreich exportiert! ${response.count || 0} Termine exportiert.`);
      } else {
        setError('Fehler beim Export der Organisation Leads: ' + response.error);
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      setError('Fehler beim Export der Organisation Leads: ' + error.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgentur = async (agenturData) => {
    setAgenturLoading(true);
    setError('');
    try {
      const result = await createAgentur(agenturData.name, agenturData.beschreibung, user.organization_id);
      if (result.success) {
        setShowCreateAgenturDialog(false);
        setSuccessMessage('Agentur erfolgreich erstellt!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(result.error || 'Fehler beim Erstellen der Agentur.');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setAgenturLoading(false);
    }
  };

  const handleAgenturLeadsExport = async () => {
    const plz = plzInput.trim();
    if (!plz) {
      setError('Bitte eine Postleitzahl eingeben.');
      return;
    }
    setPlzExporting(true);
    setError('');
    setSuccessMessage('');
    try {
      const result = await downloadAgenturLeadsByPlzExcel(user.id, plz);
      if (result.success) {
        if (result.count === 0) {
          setSuccessMessage(`Keine Leads für PLZ ${plz} gefunden.`);
        } else {
          setSuccessMessage(`${result.count} Lead(s) für PLZ ${plz} exportiert.`);
        }
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        setError(result.error || 'Fehler beim Export.');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setPlzExporting(false);
    }
  };

  return (
    <>
      <div className={`settings-panel ${isOpen ? 'open' : ''}`}>
        <div className="settings-header">
          <h2>Einstellungen</h2>
          <button className="close-settings-button" onClick={onClose}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        
        <div className="settings-content">
          {/* Erfolgs-Nachricht anzeigen */}
          {successMessage && (
            <div className="settings-success-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22,4 12,14.01 9,11.01"></polyline>
              </svg>
              {successMessage}
            </div>
          )}
          
          {/* Fehlermeldung anzeigen */}
          {error && (
            <div className="settings-error-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              {error}
            </div>
          )}
          
          {/* Persönliche Einstellungen - für ALLE Benutzer */}
          <div className="settings-section">
            <h3>Persönliche Einstellungen</h3>
            <button className="settings-button" onClick={handleChangePassword}>
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <circle cx="12" cy="16" r="1"></circle>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
              Passwort ändern
            </button>
          </div>

          {/* Lead-Übersicht nach Postleitzahl - für Agenturleiter (und Admin) */}
          {(user?.role_type === 7 || isAdmin) && (
            <div className="settings-section">
              <h3>Lead-Übersicht nach Postleitzahl</h3>
              <p style={{ fontSize: '13px', color: '#666', margin: '0 0 10px' }}>
                Postleitzahl eingeben und als Excel-Datei mit allen Leads (Name, Adresse, Status usw.) herunterladen.
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={plzInput}
                onChange={(e) => setPlzInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !plzExporting) handleAgenturLeadsExport(); }}
                placeholder="Postleitzahl (z. B. 13595)"
                style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd', marginBottom: '10px', boxSizing: 'border-box' }}
              />
              <button
                className="settings-button"
                onClick={handleAgenturLeadsExport}
                disabled={plzExporting || !plzInput.trim()}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {plzExporting ? 'Exportiere...' : 'Als Excel herunterladen'}
              </button>
            </div>
          )}

          {(isAdmin || isTeamlead || isOrgLeader || isCallcenterLeiter) && (
            <>
              {/* Leads-Bereich für Admin und Organisationsleiter */}
              {(isAdmin) && (
                <div className="settings-section">
                  <h3>Leads</h3>
                  <button 
                    className="settings-button" 
                    onClick={handleOrganizationLeads}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    Alle Organisations-Leads
                  </button>
                </div>
              )}

              {/* Callcenter-Bereich für Callcenter-Rollen */}
              {(isAdmin || user?.role_type === 4 || user?.role_type === 5) && (
                <div className="settings-section">
                  <h3>Callcenter</h3>
                  <button 
                    className="settings-button" 
                    onClick={() => setShowProjectManagementModal(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                    Projekte verwalten
                  </button>
                </div>
              )}
              
              {/* Abrechnungs-Bereich nur für Organisationsleiter, Callcenter-Leiter oder User ID 15 */}
              {(isCallcenterLeiter || isOrgLeader || isAdmin) && (
                <div className="settings-section">
                  <h3>Abrechnung</h3>
                  <button 
                    className="settings-button" 
                    onClick={handleBillingReport}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                    Abrechnung erstellen
                  </button>
                </div>
              )}
              
              {isAdmin && (
                <div className="settings-section">
                  <h3>{isTeamlead ? "Team" : "Organisationen und Teams"}</h3>
                  <button 
                    className="settings-button" 
                    onClick={handleOrganizationManagement}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    </svg>
                    {isTeamlead ? "Team verwalten" : "Organisationen verwalten"}
                  </button>
                </div>
              )}

              {/* Admin, Organisationsleiter und Teamleiter können Benutzer verwalten */}
              {(isAdmin) && (
                <div className="settings-section">
                  <h3>Benutzerverwaltung</h3>
                  {/* Benutzer erstellen - für alle Rollen */}
                  <button className="settings-button" onClick={handleCreateUser}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                    Benutzer erstellen
                  </button>
                  
                  {/* Organisationsleiter können Mitglieder ihrer Organisation verwalten */}
                  {isOrgLeader && (
                    <button 
                      className="settings-button" 
                      onClick={handleMembersManagement}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      Mitglieder verwalten
                    </button>
                  )}
                </div>
              )}
              
              <div className="settings-section">
                {(isAdmin || isOrgLeader) && (
                  <>
                  <h3>Systemeinstellungen</h3>
                    <button className="settings-button">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"></circle>
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    Allgemeine Einstellungen
                  </button>
                  </>
                )}
              </div>

              {/* Lagerverwaltung - Nur für Organisationsleiter */}
              {isOrgLeader && (
                <div className="settings-section">
                  <h3>Lagerverwaltung</h3>
                  <button 
                    className="settings-button" 
                    onClick={() => {
                      navigate('/warehouse');
                      onClose();
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                      <line x1="3" y1="6" x2="21" y2="6"></line>
                      <path d="M16 10a4 4 0 0 1-8 0"></path>
                    </svg>
                    Lagerverwaltung
                  </button>
                </div>
              )}

              {/* Projekt Export */}
              {(isAdmin) && (
                <div className="settings-section">
                  <h3>Projekt Export</h3>
                  <button 
                    className="settings-button" 
                    onClick={() => setShowProjectExportModal(true)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7,10 12,15 17,10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Projekt exportieren
                  </button>
                </div>
              )}
              
            </>
          )}

          {isAdmin && (
            <div className="settings-section">
              <h3>SMS Test Funktionen</h3>
              <SmsTestPanel user={user} />
            </div>
          )}

          {isAdmin && (
            <div className="settings-section">
              <h3>E-Mail Test Funktionen</h3>
              <button className="settings-button" onClick={() => setShowEmailTestDialog(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                E-Mail Test
              </button>
            </div>
          )}

          {isOrgLeader && (
            <div className="settings-section">
              <h3>Abrechnungen Export</h3>
              <button 
                className="settings-button" 
                onClick={handleExportOrganizationLeads}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {loading ? 'Exportiere Organisation Leads...' : 'Organisation Leads Downloaden'}
              </button>
            </div>
          )}

          {(isAdmin || isOrgLeader) && currentOrganizationId && (
            <div className="settings-section">
              <h3>Datenübertragung</h3>
              <button 
                className="settings-button" 
                onClick={() => handleDownloadTerminFiles()}
                disabled={loading}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7,10 12,15 17,10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                {loading ? 'Lade Termin-Dateien...' : 'Termin Dateien Downloaden'}
              </button>
              
              <button 
                className="settings-button" 
                onClick={() => setShowTransferModal(true)}
                disabled={loading}
              >
                Termin Dateien für beliebigen Tag Downloaden
              </button>
            </div>
          )}

        </div>
        
      </div>
      
      {/* Passwort-Änderungs-Dialog */}
      {showChangePasswordDialog && (
        <ChangePasswordDialog
          user={user}
          onSave={handlePasswordChangeSuccess}
          onCancel={() => setShowChangePasswordDialog(false)}
        />
      )}

      {/* E-Mail-Test-Dialog */}
      {showEmailTestDialog && (
        <EmailTestDialog
          isOpen={showEmailTestDialog}
          onClose={() => setShowEmailTestDialog(false)}
          user={user}
        />
      )}

      {/* Abrechnungs-Dialog */}
      {showBillingReportDialog && (
        <BillingReportDialog
          isOpen={showBillingReportDialog}
          onClose={() => setShowBillingReportDialog(false)}
          user={user}
          organizationId={user.organization_id}
        />
      )}

      {/* MODAL für Datumsauswahl und gezielte Übertragung */}
      {showTransferModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.3)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ background: 'white', padding: 32, borderRadius: 8, minWidth: 320, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
            <h3>Datum auswählen</h3>
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              style={{ marginBottom: 16, marginTop: 8, fontSize: 16, padding: 6 }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <button
                className="settings-button"
                onClick={async () => {
                  await handleDownloadTerminFiles(selectedDate);
                  setShowTransferModal(false);
                }}
                disabled={!selectedDate || loading || !currentOrganizationId}
              >
                Termin Dateien Downloaden
              </button>
              <button
                className="settings-button"
                onClick={() => setShowTransferModal(false)}
                style={{ background: '#eee', color: '#333' }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      <CreateAgenturDialog
        open={showCreateAgenturDialog}
        onClose={() => setShowCreateAgenturDialog(false)}
        onCreate={handleCreateAgentur}
        loading={agenturLoading}
        error={error}
      />

      {/* Project Management Modal */}
      <ProjectManagementModal
        isOpen={showProjectManagementModal}
        onClose={() => setShowProjectManagementModal(false)}
        user={user}
      />

      {/* Project Export Modal */}
      <ProjectExportModal
        isOpen={showProjectExportModal}
        onClose={() => setShowProjectExportModal(false)}
        user={user}
      />
    </>
  );
}

export default SettingsPanel;